import { Field, TextEditor } from '@approvalmax/ui';
import { ConditionWrapper, Grid, RichEditor } from '@approvalmax/ui/src/components';
import { stringHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { getStandaloneFields } from 'pages/requestForm/selectors/standalone';
import { FC, memo, useCallback } from 'react';
import bemFactory from 'react-bem-factory';
import { connect } from 'react-redux';
import { FeatureBadge } from 'shared/components';
import { useUserBetaFeatures } from 'shared/hooks';

import { AttachmentsSection, NoteForApproversSection, RequesterInstructionSection } from '../../components';
import FileDropArea from '../../components/FileDropArea';
import FileDropProvider from '../../components/FileDropProvider';
import FilesSection from '../form/FilesSection';
import StandaloneField from './StandaloneField/StandaloneField';
import StandaloneRequestFieldsSection from './StandaloneRequestFieldsSection/StandaloneRequestFieldsSection';
import { mapDispatchToProps, mapStateToProps } from './StandaloneRequestForm.config';
import { messages } from './StandaloneRequestForm.messages';
import {
    Content,
    ContentInner,
    CustomFieldsContainer,
    EditorContainer,
    PremiumBadgeStyled,
    Root,
    StyledField,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderActions,
    ToolbarHeaderName,
} from './StandaloneRequestForm.styles';
import { StandaloneRequestFormProps } from './StandaloneRequestForm.types';

const qa = bemFactory.qa('reqf-standalone-request-form');

const { standaloneConstants } = constants;

const StandaloneRequestForm: FC<StandaloneRequestFormProps> = memo((props) => {
    const {
        request,
        context,
        name,
        templateName,
        description,
        showValidationErrors,
        company,
        changeRequestDescription,
    } = props;
    const fieldValues = useSelector(getStandaloneFields);

    const showPremiumBadge = company.flags.isAllFeaturesTrialActive || company.flags.isTrialLicense;

    const isInvalidRequestName =
        showValidationErrors && (!name?.trim() || name.length > standaloneConstants.REQUEST_NAME_MAX_LENGTH);

    const isInvalidRequestDescription = showValidationErrors && Boolean(description.errorMessages);

    const onChange = useCallback(
        (value: string) => {
            const cleanedTextLength =
                stringHelpers.cleanHTMLAttributes(standaloneConstants.STANDALONE_EXCLUDED_ATTRIBUTES, value)?.length ||
                0;

            const errorMessage =
                cleanedTextLength > standaloneConstants.REQUEST_DESCRIPTION_MAX_LENGTH
                    ? messages.exceededDescriptionLengthErrorText
                    : null;

            changeRequestDescription({
                value,
                errorMessages: errorMessage ? [errorMessage] : null,
            });
        },
        [changeRequestDescription]
    );

    const { isBelgradeUIFilesUploadSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider
                    onUpload={(files) => props.uploadAttachments({ files })}
                    maxSize={context.attachmentMaxSize}
                >
                    {children}

                    <FileDropArea />
                </FileDropProvider>
            )}
        >
            <Root data-qa={qa()}>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderName>
                            <Field title={templateName} required>
                                <TextEditor
                                    qa={qa('request-name-editor')}
                                    value={name || ''}
                                    onChange={props.changeRequestName}
                                    placeholder={messages.requestNamePlaceholder}
                                    maxLength={standaloneConstants.REQUEST_NAME_MAX_LENGTH}
                                    invalid={isInvalidRequestName}
                                />
                            </Field>
                        </ToolbarHeaderName>

                        {showPremiumBadge && (
                            <PremiumBadgeStyled>
                                <FeatureBadge premium />
                            </PremiumBadgeStyled>
                        )}

                        <ToolbarHeaderActions />
                    </ToolbarHeader>

                    <StandaloneRequestFieldsSection />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <NoteForApproversSection />

                        <RequesterInstructionSection instructionText={context.requesterInstruction} />

                        <EditorContainer>
                            <Field title={messages.descriptionLabel} required>
                                <RichEditor
                                    dataQa={qa('request-description-editor')}
                                    value={description.value}
                                    placeholder={messages.descriptionPlaceholder}
                                    onChange={onChange}
                                    invalid={isInvalidRequestDescription}
                                />
                            </Field>

                            <CustomFieldsContainer>
                                {fieldValues.map((fieldValue) => (
                                    <StyledField key={fieldValue.field.id} title={fieldValue.field.name} size='size-11'>
                                        <StandaloneField
                                            fieldValue={fieldValue}
                                            showValidationErrors={showValidationErrors}
                                        />
                                    </StyledField>
                                ))}
                            </CustomFieldsContainer>
                        </EditorContainer>

                        {isBelgradeUIFilesUploadSection ? (
                            <Grid gridTemplateColumns='50%' gap={16}>
                                <AttachmentsSection request={request} />
                            </Grid>
                        ) : (
                            <FilesSection />
                        )}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

export default connect(mapStateToProps, mapDispatchToProps)(StandaloneRequestForm);
